import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, Fab, IconButton, Paper, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack as ArrowBackIcon } from '../icons';
import { AutoAwesome as AutoAwesomeIcon } from '../icons';
import { ChevronRight as ChevronRightIcon } from '../icons';
import { Close as CloseIcon } from '../icons';
import { Send as SendIcon } from '../icons';
import { SupportAgent as SupportAgentIcon } from '../icons';
import { useSupportAssistant } from './SupportAssistantContext';
import supportFaqData from './supportFaqData';
import { bubbleSx, onAccent, typingDotsSx } from './supportChatStyles';
import { T } from './Translate';
import { useNavigate } from 'react-router-dom';
import AiLinkButtons from '../components/AiLinkButtons';
import { brandColors } from '../theme';

const ONLINE_GREEN = '#2ecc71';

const accentButtonSx = {
  bgcolor: brandColors.accent,
  color: onAccent,
  boxShadow: 'none',
  '&:hover': { bgcolor: brandColors.accent, opacity: 0.88, boxShadow: 'none' },
};

// Ícono sobre un cuadradito o círculo con el accent suavizado.
const IconTile = ({ icon: Icon, size = 36, iconSize = 20, round = false }) => (
  <Box
    aria-hidden="true"
    sx={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: round ? '50%' : '10px',
      display: 'grid',
      placeItems: 'center',
      bgcolor: alpha(brandColors.accent, 0.14),
      color: brandColors.accent,
    }}
  >
    <Icon sx={{ fontSize: iconSize }} />
  </Box>
);

// Asistente flotante de toda la app (lo monta AppRoutes en App.js): burbuja
// fija en la esquina inferior derecha que, al abrirse, muestra un menú de
// categorías de FAQ (supportFaqData) o el chat con la IA (Qwen, ver
// SupportAssistantContext). La IA puede devolver links a publicaciones y
// derivar al chat de soporte con una persona (/support/chat).
const SupportAssistantWidget = () => {
  const { isOpen, openAssistant, closeAssistant, messages, sendMessage, loading } = useSupportAssistant();
  const [view, setView] = useState('menu'); // 'menu' | 'category' | 'chat'
  const [activeCategory, setActiveCategory] = useState(null);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (view === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view, loading]);

  const headerTitle = view === 'category' && activeCategory ? activeCategory.label : <T>Asistente Notiline</T>;

  const openCategory = (category) => {
    setActiveCategory(category);
    setView('category');
  };

  const backToMenu = () => {
    setActiveCategory(null);
    setView('menu');
  };

  const startChat = () => {
    setView('chat');
  };

  const talkToPerson = () => {
    closeAssistant();
    navigate('/support/chat', { state: { requestHuman: true } });
  };

  const handleSend = (event) => {
    event.preventDefault();
    if (!input.trim() || loading) return;
    sendMessage(input.trim());
    setInput('');
  };

  if (!isOpen) {
    return (
      <Fab
        onClick={openAssistant}
        sx={{
          ...accentButtonSx,
          position: 'fixed',
          bottom: '70px',
          right: '16px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
          zIndex: 1300,
          '&:hover': { bgcolor: brandColors.accent, opacity: 0.92 },
        }}
        aria-label="Abrir asistente de soporte"
      >
        <AutoAwesomeIcon />
      </Fab>
    );
  }

  const lastMessage = messages[messages.length - 1];

  return (
    <Paper
      elevation={0}
      role="dialog"
      aria-label="Asistente Notiline"
      sx={{
        position: 'fixed',
        bottom: '90px',
        right: '16px',
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        height: 540,
        maxHeight: 'calc(100vh - 114px)',
        bgcolor: 'background.default',
        backgroundImage: 'none',
        border: 1,
        borderColor: 'divider',
        borderRadius: '20px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1300,
        transformOrigin: 'bottom right',
        animation: 'supportWidgetIn 0.18s ease-out',
        '@keyframes supportWidgetIn': {
          from: { opacity: 0, transform: 'translateY(8px) scale(0.97)' },
          to: { opacity: 1, transform: 'none' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {view !== 'menu' && (
          <IconButton size="small" onClick={backToMenu} aria-label="Volver" sx={{ color: 'text.primary', ml: -0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <IconTile icon={view === 'category' && activeCategory ? activeCategory.icon : AutoAwesomeIcon} round />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25, color: 'text.primary' }}>
            {headerTitle}
          </Typography>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.625, fontSize: '0.72rem', color: 'text.secondary' }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: ONLINE_GREEN }} />
            <T>En línea</T>
          </Typography>
        </Box>
        <IconButton size="small" onClick={closeAssistant} aria-label="Cerrar asistente" sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
        {view === 'menu' && (
          <>
            <Typography sx={{ px: 0.5, mb: 1.25, fontSize: '0.85rem', lineHeight: 1.45, color: 'text.secondary' }}>
              <T>Hola, soy el asistente de Notiline. Elegí una categoría para ver soluciones rápidas o escribime lo que necesites:</T>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {supportFaqData.map((category) => (
                <ButtonBase
                  key={category.id}
                  onClick={() => openCategory(category)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: '100%',
                    p: 1,
                    textAlign: 'left',
                    borderRadius: '14px',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: brandColors.accent },
                  }}
                >
                  <IconTile icon={category.icon} />
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3, color: 'inherit' }}>
                    {category.label}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                </ButtonBase>
              ))}
            </Box>
          </>
        )}

        {view === 'category' && activeCategory && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activeCategory.questions.map((item, idx) => (
              <Box key={idx} sx={{ p: 1.5, borderRadius: '14px', border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.35, color: 'text.primary', mb: 0.5 }}>
                  {item.q}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.5, color: 'text.secondary' }}>{item.a}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {view === 'chat' && (
          <>
            {messages.map((m, idx) => {
              const mine = m.role === 'user';
              const firstInGroup = messages[idx - 1]?.role !== m.role;
              const lastInGroup = messages[idx + 1]?.role !== m.role;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: mine ? 'flex-end' : 'flex-start',
                    gap: 1,
                    mt: idx === 0 ? 0 : firstInGroup ? 1.5 : 0.375,
                  }}
                >
                  {!mine && (
                    <Box sx={{ visibility: firstInGroup ? 'visible' : 'hidden' }}>
                      <IconTile icon={AutoAwesomeIcon} size={28} iconSize={15} round />
                    </Box>
                  )}
                  <Box
                    sx={{
                      ...bubbleSx(mine, firstInGroup, lastInGroup),
                      maxWidth: '82%',
                      minWidth: 0,
                      flex: m.links?.length ? 1 : undefined,
                    }}
                  >
                    {/* color "inherit": el tema le fija el color a todo Typography
                        y sin esto ignora el de la burbuja. */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'inherit', fontSize: '0.85rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {m.text}
                    </Typography>
                    <AiLinkButtons links={m.links} onNavigate={closeAssistant} />
                    {m.handoff && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SupportAgentIcon />}
                        onClick={talkToPerson}
                        sx={{ ...accentButtonSx, mt: 1, textTransform: 'none', borderRadius: 999, px: 1.5 }}
                      >
                        <T>Hablar con una persona</T>
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
            {loading && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }} role="status" aria-label="El asistente está escribiendo">
                <IconTile icon={AutoAwesomeIcon} size={28} iconSize={15} round />
                <Box sx={{ ...bubbleSx(false, true, true), display: 'flex', gap: 0.5, py: 1.5, ...typingDotsSx }}>
                  <span />
                  <span />
                  <span />
                </Box>
              </Box>
            )}
            {messages.length > 1 && !loading && !lastMessage.handoff && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SupportAgentIcon sx={{ fontSize: 16 }} />}
                  onClick={talkToPerson}
                  sx={{ textTransform: 'none', borderRadius: 999, px: 1.5, color: brandColors.accent, borderColor: alpha(brandColors.accent, 0.5) }}
                >
                  <T>¿No se resolvió? Hablar con una persona</T>
                </Button>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {view !== 'chat' && (
        <Box sx={{ p: 1.25, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={startChat}
            sx={{ ...accentButtonSx, py: 1, textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            <T>Preguntarle al asistente</T>
          </Button>
        </Box>
      )}

      {view === 'chat' && (
        <Box
          component="form"
          onSubmit={handleSend}
          sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.25, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}
        >
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) handleSend(e);
            }}
            placeholder="Escribí tu consulta..."
            size="small"
            fullWidth
            multiline
            maxRows={3}
            inputProps={{ maxLength: 2000, 'aria-label': 'Escribí tu consulta' }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px', px: 1.75, fontSize: '0.875rem' } }}
          />
          <IconButton
            type="submit"
            disabled={!input.trim() || loading}
            aria-label="Enviar"
            sx={{
              ...accentButtonSx,
              flexShrink: 0,
              width: 40,
              height: 40,
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

export default SupportAssistantWidget;
